import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import TaskSubNav from '../components/TaskSubNav';
import TaskAttachments from '../components/TaskAttachments';
import { DSBackLink, DSButton, DSTextarea, DSCard, DSIcon, DSBadge } from '../design-system/primitives';
import StatusBadge from '../components/StatusBadge';
import './taskManagementShared.css';

function TaskBountyConfirm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [notify, setNotify] = useState(true);
  const [showRejectForm, setShowRejectForm] = useState(false);

  useEffect(() => {
    apiService.getTask(id)
      .then((res) => setTask(res.data))
      .catch((err) => setError(err.response?.data?.error || 'Unable to load task'))
      .finally(() => setLoading(false));
  }, [id]);

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      await apiService.confirmTaskBounty(id);
      navigate('/tasks/needs-attention', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to confirm bounty');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      setSubmitting(true);
      await apiService.undoTaskCompletion(id, { reason: rejectReason || undefined, notify });
      navigate('/tasks/needs-attention', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject completion');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-inline">Loading...</div>;
  }

  if (!task || task.status !== 'pending_confirmation') {
    return <div className="error-message">This task is not awaiting bounty confirmation</div>;
  }

  const completionEntry = (task.activity || []).find((a) => a.action === 'completed');

  return (
    <div className="tm-page">
      <DSBackLink onClick={() => navigate(-1)}>Back</DSBackLink>
      <div className="tm-section">
        <h1>Confirm Bounty</h1>
        {error && <div className="error-message">{error}</div>}

        <DSCard variant="elevated" bgVariant="default" borderVariant="subtle" cornerVariant="lg" className="tm-suggestion-card">
          <span className="tm-suggestion-meta">COMPLETED TASK</span>
          <h3>{task.title}</h3>
          {task.description && <p className="tm-preserve-lines">{task.description}</p>}
          <TaskAttachments owner="task" ownerId={task.id} />
          <div className="tm-task-tags">
            <DSBadge variant="brand" size="sm" leadingIcon={<DSIcon name="star" />}>{task.point_value} pts awarded</DSBadge>
            <StatusBadge variant="success" size="sm" leadingIcon={<DSIcon name="checkCircle" />}>Completed</StatusBadge>
          </div>
        </DSCard>

        {completionEntry && (
          <DSCard variant="elevated" bgVariant="default" borderVariant="subtle" cornerVariant="lg" className="tm-suggestion-card">
            <span className="tm-suggestion-meta">COMPLETED BY</span>
            <strong>{completionEntry.performed_by_name || 'Unknown'}</strong>
            <div className="tm-suggestion-meta">
              Completed {new Date(completionEntry.created_at).toLocaleDateString()} · {task.point_value} pts already awarded
            </div>
            {task.current_completion_id && (
              <TaskAttachments owner="completion" ownerId={task.current_completion_id} title="Completion screenshots" />
            )}
          </DSCard>
        )}

        <div className="tm-warning-box">
          <DSIcon name="trophy" /> BOUNTY TO PAY OUT: <strong>{formatCurrency(task.bounty_amount)}</strong>
          <br />Credits will be added immediately upon confirmation.
        </div>

        {!showRejectForm && (
          <div className="tm-actions">
            <DSButton appearance="solid" tone="primary" onClick={handleConfirm} disabled={submitting} leadingIcon={<DSIcon name="trophy" />}>Confirm & Pay Out {formatCurrency(task.bounty_amount)}
            </DSButton>
            <DSButton appearance="solid" tone="danger" onClick={() => setShowRejectForm(true)} disabled={submitting}>
              <DSIcon name="xCircle" /> Reject & Don't Pay
            </DSButton>
          </div>
        )}

        {showRejectForm && (
          <>
            <div className="tm-form-group">
              <DSTextarea label="Reason (optional)" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Explain why this completion is being rejected..." />
              <div className="tm-suggestion-meta">The member will see this note in their notification.</div>
            </div>
            <div className="tm-form-group">
              <label>
                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
                {' '}Notify the member about this rejection
              </label>
            </div>
            <div className="tm-actions">
              <DSButton appearance="solid" tone="danger" onClick={handleReject} disabled={submitting}>
                {submitting ? 'Rejecting...' : 'Confirm Rejection'}
              </DSButton>
              <DSButton appearance="outline" tone="neutral" onClick={() => setShowRejectForm(false)} disabled={submitting}>Cancel</DSButton>
            </div>
          </>
        )}
        <p className="tm-audit-note">Rejecting will return the task to open status.</p>
      </div>
      <TaskSubNav />
    </div>
  );
}

export default TaskBountyConfirm;
